import { gql, useMutation } from '@apollo/client';
import {
  createBindingTask as CREATE_BINDING_TASK,
  Binding,
  BindingStatus,
  TaskStatus,
} from '@admiin-com/ds-graphql';
import { convertToBackendTask } from '../../helpers/tasks';
import { useGetTaskInput } from '../../features/providers/TaskCreationProvider/useGetTaskInput';
import { BillCreateFormData } from '../../features/providers/TaskCreationProvider/TaskCreationProvider';

type BindingWithExtras = Binding & { sendSMS?: boolean | null };

export function useCreateBindingTask(): [
  (options: {
    tasks: BillCreateFormData[];
    sendTemplate?: boolean | null;
    templateHtml?: string | null;
    templateJson?: string | null;
    includeLetterhead?: boolean | null;
    bindingId?: string;
    emailMessage?: string | null;
    sendSMS?: boolean | null;
  }) => Promise<any>,
  { error: any }
] {
  const [mutate, { error }] = useMutation(gql(CREATE_BINDING_TASK));
  const createTaskInputValues = useGetTaskInput();

  const createBindingTask = async (options: {
    tasks: BillCreateFormData[];
    sendTemplate?: boolean | null;
    templateHtml?: string | null;
    templateJson?: string | null;
    includeLetterhead?: boolean | null;
    bindingId?: string;
    emailMessage?: string | null;
    sendSMS?: boolean | null;
  }) => {
    const tasksInput = await Promise.all(
      options.tasks.map(async (task) => {
        const taskInput = await createTaskInputValues(
          {
            ...task,
            emailMessage: options.emailMessage ?? null,
          },
          {
            taskType: 'tax',
            savingAsDraft: false,
          }
        );
        if (options.bindingId) {
          taskInput.bindingId = options.bindingId;
        }
        return taskInput;
      })
    );
    const backendTasks = tasksInput.map((t) => convertToBackendTask(t as any));
    const bindingStatus = backendTasks.some(
      (t) => t?.status === TaskStatus.REVIEW
    )
      ? BindingStatus.REVIEW
      : backendTasks.some((t) => t?.status === TaskStatus.DRAFT)
      ? BindingStatus.DRAFT
      : BindingStatus.SENT;
    const result = await mutate({
      variables: {
        input: {
          tasks: backendTasks,
          sendTemplate: options.sendTemplate,
          templateHtml: options.templateHtml ?? null,
          templateJson: options.templateJson ?? null,
          includeLetterhead: options.includeLetterhead ?? null,
          status: bindingStatus,
          emailMessage: options.emailMessage ?? null,
          sendSMS: options.sendSMS ?? null,
        },
      },
    });

    const bindingId =
      backendTasks.find((task) => task?.bindingId)?.bindingId ??
      options.bindingId ??
      result?.data?.createBindingTask?.[0]?.bindingId ??
      null;

    const binding: BindingWithExtras | null = bindingId
      ? {
          __typename: 'Binding',
          id: bindingId,
          status: bindingStatus,
          sendTemplate: options.sendTemplate ?? null,
          emailMessage: options.emailMessage ?? null,
          sendSMS: options.sendSMS ?? null,
        }
      : null;

    return {
      ...result,
      binding,
    };
  };

  return [createBindingTask, { error }];
}
